import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { trackPurchaseClick } from '@/lib/stats/track';

import { getPortalLinks } from '../../../config/portalLinks';
import { useAppStore } from '../../../store';
import { useRegion } from '../../hooks/useRegion';
import CategoryIcon from '../SDKCategory/CategoryIcon';
import CategoryTitle from '../SDKCategory/CategoryTitle';

interface CategoryAreaProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
}

export const CategoryArea = ({ isSidebarOpen, onToggleSidebar }: CategoryAreaProps) => {
  const { currentSDK, selectedFeature } = useAppStore();
  const { t, i18n } = useTranslation();
  const { isOverseas, isLoading } = useRegion();
  const location = useLocation();

  // 라이선스 페이지인지 확인
  const isLicensePage = location.pathname.replace(/^\/en/, '') === '/license';

  // currentSDK로 카테고리 정보 제공
  // selectedFeature.category가 유효한 SDK 타입이면 우선 사용
  // (라이선스 페이지에서 SDK로 이동 중인 상태 처리)
  const getCurrentSDKCategory = () => {
    const validSDKTypes = ['hwp', 'hcell', 'ocr', 'docdiff', 'dataloader'];
    if (selectedFeature?.category && validSDKTypes.includes(selectedFeature.category)) {
      return { id: selectedFeature.category };
    }
    if (isLicensePage) {
      return { id: 'license' };
    }
    if (currentSDK) {
      return { id: currentSDK };
    }
    return null;
  };

  const currentSDKCategory = getCurrentSDKCategory();

  const portalLinks = useMemo(() => getPortalLinks(i18n.language, isOverseas), [i18n.language, isOverseas]);

  const purchaseUrl = useMemo(() => {
    // 라이선스 페이지에서는 구매하기 버튼 숨김
    if (isLicensePage) return undefined;
    if (!currentSDK) return undefined;

    // 해외향일 때는 다른 SDK ID 사용
    const sdkIdMap: Record<string, number> = isOverseas
      ? {
          hcell: 9, // Spreadsheet SDK
          ocr: 8, // OCR SDK
        }
      : {
          hwp: 1,
          hcell: 2,
          docdiff: 3,
          ocr: 4,
          auth: 5,
        };

    const sdkId = sdkIdMap[currentSDK];
    if (!sdkId) return undefined;

    return `${portalLinks.purchase}?sdk_id=${sdkId}`;
  }, [currentSDK, portalLinks.purchase, isOverseas, isLicensePage]);

  const purchaseLabel = t('purchase');

  const dataloaderGetApiUrl = useMemo(() => {
    if (currentSDK !== 'dataloader') return undefined;
    return portalLinks.mypageApi;
  }, [currentSDK, portalLinks.mypageApi]);

  const dataloaderGetApiLabel = t('menu.dataloaderGetApi');

  // Region 정보 로딩 중일 때는 렌더링하지 않음
  // 이렇게 하면 해외향일 때 HWP SDK가 잠깐 보이는 것을 방지
  if (isLoading) {
    return null;
  }

  // SDK 카테고리가 선택된 경우
  if (currentSDKCategory) {
    return (
      <div className='bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5'>
        <div className='flex items-center justify-between min-w-0'>
          <div className='flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1'>
            {!isSidebarOpen && (
              <button
                onClick={onToggleSidebar}
                className='p-2 rounded-md hover:bg-gray-100 transition-colors mr-1 sm:mr-2 flex-shrink-0'
                title={t('sidebar.openSidebar')}
              >
                <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 6h16M4 12h16M4 18h16'
                  />
                </svg>
              </button>
            )}
            {isLicensePage ? (
              <div className='w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg'>
                <svg className='w-6 h-6 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                  />
                </svg>
              </div>
            ) : (
              currentSDK && <CategoryIcon sdkType={currentSDK} />
            )}
            <div className='min-w-0 flex-1'>
              <CategoryTitle categoryName={currentSDKCategory.id} />
            </div>
          </div>
          {purchaseUrl && (
            <div className='flex-shrink-0'>
              <a
                href={purchaseUrl}
                onClick={() => currentSDK && trackPurchaseClick(currentSDK)}
                className='inline-flex items-center gap-2.5 bg-gradient-to-r from-[#1465f4] to-[#00399e] px-8 py-2 rounded-[200px] text-white font-bold text-base uppercase whitespace-nowrap hover:from-[#1050f0] hover:to-[#002d8a] transition-all duration-200'
              >
                {purchaseLabel}
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <g clipPath='url(#clip0_69_20)'>
                    <path
                      d='M8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16ZM4.8 7.2H9.268L7.434 5.366C7.122 5.054 7.122 4.548 7.434 4.234C7.746 3.922 8.254 3.922 8.566 4.234L11.766 7.434C11.804 7.472 11.836 7.512 11.866 7.556C11.88 7.576 11.888 7.596 11.9 7.618C11.914 7.644 11.93 7.668 11.94 7.694C11.95 7.72 11.958 7.748 11.966 7.776C11.972 7.798 11.982 7.82 11.986 7.844C11.996 7.896 12.002 7.948 12.002 8C12.002 8.052 11.996 8.104 11.986 8.156C11.982 8.18 11.972 8.202 11.966 8.224C11.958 8.252 11.952 8.278 11.94 8.306C11.928 8.332 11.914 8.358 11.9 8.382C11.888 8.402 11.88 8.424 11.866 8.444C11.836 8.488 11.804 8.528 11.766 8.566L8.566 11.766C8.41 11.922 8.206 12 8 12C7.794 12 7.59 11.922 7.434 11.766C7.122 11.454 7.122 10.948 7.434 10.634L9.268 8.8H4.8C4.358 8.8 4 8.442 4 8C4 7.558 4.358 7.2 4.8 7.2Z'
                      fill='white'
                    />
                  </g>
                  <defs>
                    <clipPath id='clip0_69_20'>
                      <rect width='16' height='16' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
              </a>
            </div>
          )}

          {dataloaderGetApiUrl && (
            <div className='flex-shrink-0'>
              <a
                href={dataloaderGetApiUrl}
                onClick={() => trackPurchaseClick('dataloader')}
                className='inline-flex items-center gap-2.5 bg-gradient-to-r from-[#1465f4] to-[#00399e] px-8 py-2 rounded-[200px] text-white font-bold text-base uppercase whitespace-nowrap hover:from-[#1050f0] hover:to-[#002d8a] transition-all duration-200'
              >
                {dataloaderGetApiLabel}
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <g clipPath='url(#clip0_69_20)'>
                    <path
                      d='M8 16C12.418 16 16 12.418 16 8C16 3.582 12.418 0 8 0C3.582 0 0 3.582 0 8C0 12.418 3.582 16 8 16ZM4.8 7.2H9.268L7.434 5.366C7.122 5.054 7.122 4.548 7.434 4.234C7.746 3.922 8.254 3.922 8.566 4.234L11.766 7.434C11.804 7.472 11.836 7.512 11.866 7.556C11.88 7.576 11.888 7.596 11.9 7.618C11.914 7.644 11.93 7.668 11.94 7.694C11.95 7.72 11.958 7.748 11.966 7.776C11.972 7.798 11.982 7.82 11.986 7.844C11.996 7.896 12.002 7.948 12.002 8C12.002 8.052 11.996 8.104 11.986 8.156C11.982 8.18 11.972 8.202 11.966 8.224C11.958 8.252 11.952 8.278 11.94 8.306C11.928 8.332 11.914 8.358 11.9 8.382C11.888 8.402 11.88 8.424 11.866 8.444C11.836 8.488 11.804 8.528 11.766 8.566L8.566 11.766C8.41 11.922 8.206 12 8 12C7.794 12 7.59 11.922 7.434 11.766C7.122 11.454 7.122 10.948 7.434 10.634L9.268 8.8H4.8C4.358 8.8 4 8.442 4 8C4 7.558 4.358 7.2 4.8 7.2Z'
                      fill='white'
                    />
                  </g>
                  <defs>
                    <clipPath id='clip0_69_20'>
                      <rect width='16' height='16' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
  // 사이드바가 닫혀있는 경우 기본 헤더
  else if (!isSidebarOpen) {
    return (
      <div className='bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5'>
        <div className='flex items-center justify-between min-w-0'>
          <div className='flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1'>
            <button
              onClick={onToggleSidebar}
              className='p-2 rounded-md hover:bg-gray-100 transition-colors flex-shrink-0'
              title='사이드바 열기'
            >
              <svg className='w-5 h-5 text-gray-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            </button>
            <h1 className='text-xl sm:text-2xl font-bold text-black truncate'>{t('sidebar.title')}</h1>
          </div>
        </div>
      </div>
    );
  }

  // 사이드바가 열려있고 SDK 카테고리가 없는 경우 헤더 숨김
  return null;
};
